<template>
    <div :class="$style.tileItem" @click="handleCardClick" style="cursor:pointer">
        <!-- 썸네일 영역 (왼쪽) -->
        <div :class="$style.thumbnailWrapper">
            <div :class="$style.imgDiv">
                <span v-if="showDraftBadge" :class="$style.draftBadge">임시저장</span>
                <span v-else-if="showPendingBadge" :class="$style.pendingBadge">승인 대기</span>
                <img v-if="commission.thumbnailUrl" :src="commission.thumbnailUrl" :alt="commission.name"
                    :class="$style.poster" />
                <div v-else :class="$style.imagePlaceholder">
                    <div :class="$style.placeholderIcon">🎨</div>
                    <div :class="$style.placeholderText">{{ commission.name }}</div>
                </div>
            </div>
        </div>

        <!-- 정보 영역 (중앙) -->
        <div :class="$style.info">
            <div :class="$style.infoTop">
                <div :class="$style.title">{{ commission.name }}</div>
                <div :class="$style.hashtagRow">
                    <span v-for="tag in displayHashtags" :key="tag" :class="$style.hashtagChip">
                        #{{ tag }}
                    </span>
                </div>
                <div v-if="artistName" :class="$style.artist">
                    <span :class="$style.artistAvatar">
                        <img v-if="commission.createdByProfileUrl" :src="commission.createdByProfileUrl"
                            :alt="artistName" :class="$style.artistAvatarImg" />
                        <span v-else :class="$style.artistAvatarFallback">{{ artistInitial }}</span>
                    </span>
                    <span :class="$style.artistName">{{ artistName }}</span>
                </div>
            </div>

            <div v-if="hasBasePrice" :class="$style.price">
                <span :class="$style.priceValue">{{ formattedBasePrice }}</span>
                <span :class="$style.priceFrom">부터</span>
            </div>
        </div>

        <!-- 우측 사이드: 카테고리 뱃지(위) + 찜하기(아래) -->
        <div :class="$style.sideColumn">
            <span v-if="commission.category" :class="[$style.categoryBadge, $style[`cat-${commission.category}`]]">
                {{ categoryLabel(commission.category) }}
            </span>
            <span v-else :class="$style.categoryBadgeSpacer" aria-hidden="true"></span>

            <button v-if="showLikeButton" :class="[$style.likeBtnSide, { [$style.liked]: isLiked }]"
                @click.stop="handleLikeClick" :disabled="disabled" aria-label="찜하기">
                <img v-if="isLiked" :class="$style.likeHeart" src="@/assets/images/heart.png" alt="찜됨" />
                <img v-else :class="$style.likeHeart" src="@/assets/images/heart_lightgray.png" alt="찜하기" />
            </button>
        </div>
    </div>
</template>

<script>
import { useUserStore } from "@/store/user";

export default {
    name: "CommissionCard",
    props: {
        commission: {
            type: Object,
            required: true,
            validator(commission) {
                return commission && typeof commission.id === "string";
            },
        },
        isLiked: {
            type: Boolean,
            default: false,
        },
        showLikeButton: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["like-toggle"],
    computed: {
        userStore() {
            return useUserStore();
        },
        showDraftBadge() {
            const st = this.commission.status || "published";
            if (st !== "draft") return false;
            return this.userStore.isLogin && this.commission.createdBy === this.userStore.email;
        },
        showPendingBadge() {
            const st = this.commission.status || "published";
            if (st === "draft") return false;
            if (this.commission.approved !== false) return false;
            const isCreator =
                this.userStore.isLogin && this.commission.createdBy === this.userStore.email;
            return this.userStore.auth === "admin" || isCreator;
        },
        hasBasePrice() {
            const p = this.commission.basePrice;
            if (p === null || p === undefined || p === "") return false;
            const num = Number(p);
            return Number.isFinite(num) && num >= 0;
        },
        formattedBasePrice() {
            const num = Number(this.commission.basePrice) || 0;
            return `${new Intl.NumberFormat("ko-KR").format(num)}원`;
        },
        artistName() {
            const nickname = (this.commission.createdByNickname || "").trim();
            if (nickname) return nickname;
            const email = (this.commission.createdBy || "").trim();
            if (email) return email.split("@")[0];
            return "";
        },
        artistInitial() {
            const name = this.artistName;
            if (!name) return "?";
            return name.charAt(0).toUpperCase();
        },
        displayHashtags() {
            const raw = this.commission.hashtags;
            if (!Array.isArray(raw)) return [];
            const maxTags = 3;
            return raw
                .map(t => String(t || "").replace(/^#+/, "").trim())
                .filter(Boolean)
                .slice(0, maxTags);
        },
    },
    methods: {
        categoryLabel(value) {
            const map = {
                design: "디자인",
                illustration: "일러스트",
                video: "영상",
                music: "음악",
                etc: "기타",
            };
            return map[value] || "기타";
        },
        handleCardClick(event) {
            this.$router.push({ name: "CommissionDetail", params: { id: this.commission.id } });
            event.target.blur();
        },
        handleLikeClick() {
            this.$emit("like-toggle", this.commission.id);
        },
    },
};
</script>

<style module>
/* ========================================
   Base (> 480px) — PC/태블릿 공통 카드 스타일
   ======================================== */
.tileItem {
    box-sizing: border-box;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 16px;
    transition: all 0.25s ease;
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    border: 1px solid rgba(139, 107, 184, 0.08);
    box-shadow: 0 2px 12px rgba(45, 27, 78, 0.04);
    min-width: 0;
}

.tileItem:hover {
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 6px 24px rgba(45, 27, 78, 0.1);
}

/* 썸네일 래퍼 (왼쪽) */
.thumbnailWrapper {
    flex-shrink: 0;
    width: 140px;
    padding: 10px;
}

.imgDiv {
    position: relative;
    display: block;
    width: 100%;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    border-radius: 14px;
    background: #f5f5f5;
    box-shadow: 0 4px 16px rgba(45, 27, 78, 0.1);
}

.imgDiv img.poster {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 14px;
}

.draftBadge {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 8;
    padding: 4px 8px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 700;
    font-family: "NanumSquareRoundB", sans-serif;
    color: #fff;
    background: rgba(102, 189, 255, 0.92);
    border: 1px solid rgba(255, 255, 255, 0.35);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    pointer-events: none;
}

.pendingBadge {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 8;
    padding: 4px 8px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 700;
    font-family: "NanumSquareRoundB", sans-serif;
    color: #fff;
    background: rgba(255, 152, 0, 0.92);
    border: 1px solid rgba(255, 255, 255, 0.35);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    pointer-events: none;
}

.sideColumn {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 14px 10px 14px 0;
    align-self: stretch;
}

.categoryBadge {
    flex-shrink: 0;
    display: inline-flex;
    margin-right: 5px;
    align-items: center;
    justify-content: center;
    height: 20px;
    padding: 0 8px;
    border-radius: 20px;
    font-size: 0.62rem;
    font-weight: 700;
    font-family: "NanumSquareRoundEB", sans-serif;
    letter-spacing: 0.3px;
    white-space: nowrap;
}

.categoryBadgeSpacer {
    display: block;
    height: 20px;
}

.cat-poster,
.cat-mixing,
.cat-illustration,
.cat-design,
.cat-video,
.cat-etc {
    background: rgba(102, 189, 255, 0.15);
    color: #3b8ec8;
    border: 1px solid rgba(102, 189, 255, 0.3);
}

.likeBtnSide {
    flex-shrink: 0;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: end;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 6px;
    border-radius: 12px;
    transition: background 0.15s ease, transform 0.15s ease;
}


.likeBtnSide:active:not(:disabled) {
    transform: scale(0.95);
}

.likeHeart {
    width: 28px !important;
    height: 28px !important;
    padding: 4px;
    border-radius: 50% !important;
    border: 1px solid #e0e0e0;
    object-fit: contain !important;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(45, 27, 78, 0.08);
}

.likeBtnSide.liked .likeHeart {
    border-color: #ff9fb6;
    background-color: #fff0f5;
}

.likeBtnSide:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.imagePlaceholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
    border-radius: 14px;
    padding: 20px;
    box-sizing: border-box;
}

.placeholderIcon {
    font-size: 3rem;
    margin-bottom: 12px;
    opacity: 0.5;
}

.placeholderText {
    font-size: 0.875rem;
    color: #666;
    text-align: center;
    font-family: "NanumSquareRoundR", sans-serif;
    word-break: keep-all;
    line-height: 1.4;
    padding: 0 10px;
}

/* 정보 영역 (중앙) */
.info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 12px 14px 4px;
    min-width: 0;
}

.infoTop {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.title {
    font-family: "Paperlogy7", "NanumSquareRoundEB", "NanumSquareRoundB", sans-serif;
    font-weight: 700;
    text-align: left;
    font-size: 1.1rem;
    color: #2d1b4e;
    line-height: 1.35;
    letter-spacing: -0.2px;
    word-break: keep-all;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.hashtagRow {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    min-width: 0;
    overflow: hidden;
    height: 20px;
}

.hashtagChip {
    display: inline-flex;
    align-items: center;
    height: 20px;
    border-radius: 20px;
    font-family: "NanumSquareRoundB", sans-serif;
    font-size: 0.65rem;
    font-weight: 600;
    color: #3b8ec8;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
}

.artist {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-family: "NanumSquareRoundR", sans-serif;
    font-size: 0.8rem;
    color: #7a6b94;
    line-height: 1.2;
    min-width: 0;
}

.artistAvatar {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(135deg, #dcefff 0%, #a8d4ff 100%);
    border: 1.5px solid #fff;
    box-shadow: 0 1px 4px rgba(27, 60, 120, 0.12);
    color: #fff;
}

.artistAvatarImg {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.artistAvatarFallback {
    font-family: "NanumSquareRoundB", sans-serif;
    font-size: 0.72rem;
    color: #2d5a8f;
    line-height: 1;
}

.artistName {
    font-family: "NanumSquareRoundB", sans-serif;
    color: #4a3a6a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.price {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    color: #3b8ec8;
    letter-spacing: 0.2px;
    padding-top: 4px;
    border-top: 1px dashed rgba(139, 107, 184, 0.18);
}

.priceValue {
    font-family: "Paperlogy7", "NanumSquareRoundEB", sans-serif;
    font-size: 1.05rem;
    font-weight: 700;
}

.priceFrom {
    font-family: "NanumSquareRoundR", sans-serif;
    font-size: 0.7rem;
    color: #9a8db3;
}

/* ========================================
   Mobile (<= 480px) — 기본 레이아웃 유지, 크기만 축소
   ======================================== */
@media (max-width: 480px) {
    .thumbnailWrapper {
        width: 110px;
        padding: 8px;
    }

    .imgDiv {
        border-radius: 12px;
    }

    .imgDiv img.poster {
        border-radius: 12px;
    }

    .sideColumn {
        padding: 10px 6px 10px 0;
        gap: 6px;
    }

    .categoryBadge {
        height: 18px;
        padding: 0 7px;
        font-size: 0.56rem;
        margin-top: 4px;
    }

    .categoryBadgeSpacer {
        height: 18px;
    }

    .info {
        padding: 10px 8px 10px 4px;
        gap: 6px;
    }

    .infoTop {
        gap: 0px;
    }

    .title {
        font-size: 0.92rem;
        line-height: 25px;
    }

    .hashtagRow {
        gap: 3px;
        height: 18px;
    }

    .hashtagChip {
        height: 18px;
        font-size: 0.58rem;
    }

    .artist {
        font-size: 0.7rem;
        gap: 5px;
    }

    .artistAvatar {
        width: 20px;
        height: 20px;
    }

    .artistAvatarFallback {
        font-size: 0.62rem;
    }

    .artistName {
        max-width: 140px;
    }

    .price {
        padding-top: 2px;
    }

    .priceValue {
        font-size: 0.78rem;
        line-height: 20px;
    }

    .priceFrom {
        font-size: 0.62rem;
    }

    .likeBtnSide {
        padding: 4px;
    }

    .likeHeart {
        width: 28px !important;
        height: 28px !important;
        padding: 3px;
    }

    .imagePlaceholder {
        border-radius: 12px;
        padding: 12px;
    }

    .placeholderIcon {
        font-size: 1.8rem;
        margin-bottom: 6px;
    }

    .placeholderText {
        font-size: 0.7rem;
    }
}
</style>
